import React, { useState, useContext, useEffect, useRef } from 'react';
import { NavLink, Route, useLocation } from 'react-router-dom';
import HeavySetDataContext from '../../context/HeavySetDataContext';
import UserSettingsContext from '../../context/UserSettingsContext';
import { useOnClickOutside } from '../../hooks';
import { ReactComponent as HeavyDataLogoSVG } from '../../assets/logo.svg';
import Summary from './Summary';
import Workouts from '../Workouts/Workouts';
import SingleWorkout from '../Workouts/SingleWorkout';
import Exercises from '../Exercises/Exercises';
import SingleExercise from '../Exercises/SingleExercise';
import { CSVReader } from 'react-papaparse';
import Footer from '../Footer';
import styled from 'styled-components';
import CSVUpload from '../CSVUpload/CSVUpload';
import Burger from '../Burger/Burger';
import ToggleUnits from './ToggleUnits';

const DashboardWrap = styled.div`
  @media (min-width: 768px) {
    display: flex;
  }
`;

export const MobileHeader = styled.header`
  display: flex;
  align-items: center;
  position: fixed;
  top: 0;
  right: 0;
  left: 0;
  padding: 1rem;
  color: #eceff4;
  background-color: #434c5e;
  z-index: 10;

  @media (min-width: 768px) {
    display: none;
  }

  button {
    margin-right: 1.5rem;
    z-index: 11;
  }
`;

const DashboardSidebar = styled.aside`
  display: flex;
  flex-direction: column;
  transform: ${({ open }) => open ? 'translateX(0)' : 'translateX(-100%)'};
  width: 18rem;
  padding: 1rem;
  text-align: left;
  position: fixed;
  top: 67px;
  left: 0;
  bottom: 0;
  transition: transform 0.3s ease-in-out;
  background-color: #434c5e;
  overflow: auto;
  z-index: 10;
  
  @media (min-width: 768px) {
    top: 0;
    overflow: auto;
    padding: 1.5rem 1rem;
    width: 16rem;
    height: 100vh;
    background-color: #434c5e;
    box-shadow: 2px 0px 8px 0px rgba(0,0,0,0.3);
    transform: none;
  }
`;

export const LogoWrap = styled.div`
  display: flex;
  align-items: center;
`;

export const LogoIcon = styled(HeavyDataLogoSVG)`
  stroke: #fff;
  width: 1.5rem;
  height: 1.5rem;
  margin-right: 0.5rem;
  backface-visibility: hidden;
  overflow: hidden;
  vertical-align: middle;
`;

export const LogoText = styled.h1`
  color: #eceff4;
  font-size: 1.5rem;
  font-weight: 900;
  text-transform: uppercase;
`;

const DashboardNav = styled.div`
  display: flex;
  flex-direction: column;
  color: #eceff4;

  ${LogoWrap} {
    display: none;

    @media (min-width: 768px) {
      display: flex;
    }
  }
`;

const DashboardNavLinks = styled.nav`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  font-size: .875rem;

  @media (min-width: 768px) {
    margin-top: 2rem;
    padding-bottom: 0;
  }
`;

const DashboardOverview = styled.div`
  margin-bottom: 1.5rem;
`;

const DashboardOverviewTitle = styled.h3`
  margin-bottom: .5rem;
  color: #e5e9f0;
  font-size: .875rem;
  font-weight: 900;
  text-transform: uppercase;
`;

const DashboardMain = styled.main`
  width: 100%;
  min-height: 100vh;
  padding: 5.75rem 1.5rem 1.5rem;
  
  @media (min-width: 768px) {
    width: calc(100% - 16rem);
    margin-left: 16rem;
    padding: 1.5rem;
  }
`;

const Dashboard = ({ workouts, exercises, getHeavyData, getWorkouts, getExercises }) => {
  const [numOfWorkouts, setNumOfWorkouts] = useState(0);
  const data = useContext(HeavySetDataContext);

  const [open, setOpen] = useState(false);
  const isHidden = open ? true : false;

  const node = useRef();
  useOnClickOutside(node, () => setOpen(false));

  const [units, setUnits] = useState('kg');
  const userSettings = { units, setUnits };

  const { pathname } = useLocation();
  useEffect(() => {
    setOpen(false)
  }, [pathname]);

  useEffect(() => {
    setNumOfWorkouts(data.length);
  }, [data.length]);

  return (
    <>
      <DashboardWrap>
        <UserSettingsContext.Provider value={userSettings}>
          <div ref={node}>
            <MobileHeader>
              <Burger open={open} setOpen={setOpen} />

              <LogoWrap>
                <LogoIcon />
                <LogoText>HeavyData</LogoText>
              </LogoWrap>
            </MobileHeader>

            <DashboardSidebar open={open} aria-hidden={!isHidden}>
              <DashboardNav>
                <LogoWrap>
                  <LogoIcon />
                  <LogoText>HeavyData</LogoText>
                </LogoWrap>

                <DashboardNavLinks>
                  <DashboardOverview>
                    <DashboardOverviewTitle>Dashboard</DashboardOverviewTitle>
                    <NavLink to="/" exact className="NavLink" activeClassName="NavLink--selected">
                      Overview
                </NavLink>
                    <NavLink to="/upload" exact className="NavLink" activeClassName="NavLink--selected">
                      Upload CSV Data
                </NavLink>
                  </DashboardOverview>

                  <Workouts workouts={workouts} />

                  <Exercises exercises={exercises} />
                </DashboardNavLinks>
              </DashboardNav>
            </DashboardSidebar>
          </div>

          <DashboardMain>
            <ToggleUnits />

            <Route exact={true} path="/">
              {data ? <Summary numOfWorkouts={numOfWorkouts} exercises={exercises} /> : <CSVReader />}
            </Route>

            <Route exact={true} path="/upload">
              <CSVUpload
                getHeavyData={getHeavyData}
                getWorkouts={getWorkouts}
                getExercises={getExercises}
              />
            </Route>

            <Route path="/workouts/:slug">
              <SingleWorkout />
            </Route>

            <Route path="/exercises/:slug">
              <SingleExercise />
            </Route>
          </DashboardMain>
        </UserSettingsContext.Provider>
      </DashboardWrap>
      <Footer />
    </>
  );
}

export default Dashboard;
